// src/utils/skillsAssessment.js
// Shared utility for calculating trading skill metrics across components

/**
 * Calculate trading skill metrics based on trade history
 * @param {Array} trades - Array of trade objects
 * @returns {Object} Skills assessment data with scores and overall mastery score
 */
export const calculateSkillsAssessment = (trades) => {
  if (!trades || trades.length === 0) {
    return {
      entryTiming: 0,
      exitExecution: 0,
      riskManagement: 0,
      emotionalControl: 0,
      strategyAdherence: 0,
      patternRecognition: 0,
      consistency: 0,
      probability: 0,
      profitability: 0,
      stability: 0,
      discipline: 0,
      overallScore: 0,
    };
  }

  // Calculate win rate
  const winningTrades = trades.filter((trade) => trade.pnl > 0);
  const winRate =
    trades.length > 0 ? (winningTrades.length / trades.length) * 100 : 0;

  // Calculate profit factor
  const totalProfit = winningTrades.reduce((sum, trade) => sum + trade.pnl, 0);
  const losingTrades = trades.filter((trade) => trade.pnl < 0);
  const totalLoss = Math.abs(
    losingTrades.reduce((sum, trade) => sum + trade.pnl, 0)
  );
  const profitFactor =
    totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? 2 : 0;

  // Calculate risk-reward ratio where available
  const tradesWithRiskReward = trades.filter(
    (trade) => trade.risk && trade.reward
  );
  const avgRiskReward =
    tradesWithRiskReward.length > 0
      ? tradesWithRiskReward.reduce(
          (sum, trade) => sum + trade.reward / trade.risk,
          0
        ) / tradesWithRiskReward.length
      : 0;

  // Calculate average win and loss percentages
  const avgWinPercent =
    winningTrades.length > 0
      ? winningTrades.reduce((sum, trade) => {
          return (
            sum +
            ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100
          );
        }, 0) / winningTrades.length
      : 0;

  const avgLossPercent =
    losingTrades.length > 0
      ? Math.abs(
          losingTrades.reduce((sum, trade) => {
            return (
              sum +
              ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100
            );
          }, 0) / losingTrades.length
        )
      : 0;

  // Calculate pnl variance (for consistency)
  const pnls = trades.map((trade) => trade.pnl);
  const avgPnl = pnls.reduce((sum, pnl) => sum + pnl, 0) / pnls.length;
  const variance =
    pnls.reduce((sum, pnl) => sum + Math.pow(pnl - avgPnl, 2), 0) / pnls.length;
  const stdDev = Math.sqrt(variance);
  const coefficientOfVariation = avgPnl !== 0 ? stdDev / Math.abs(avgPnl) : 999;

  // Apply more strict scoring formulas based on these metrics

  // Entry Timing (based on win rate and sample size)
  // Lower win rates get penalized more heavily now
  const entryTimingBase = Math.pow(winRate / 100, 1.2) * 85;
  const entryTimingScore = Math.min(85, Math.max(5, entryTimingBase));

  // Exit Execution (based on average win % vs loss %)
  // Rewards better win:loss ratio but is stricter
  const winLossRatio =
    avgLossPercent > 0
      ? avgWinPercent / avgLossPercent
      : avgWinPercent > 0
      ? 1.5
      : 0;
  const exitScoreBase = Math.min(85, Math.pow(winLossRatio, 0.8) * 35);
  const exitScore = Math.max(10, exitScoreBase);

  // Risk Management (based on largest losses, profit factor, and position sizing)
  // More heavily penalizes large losses now
  const worstLossPercent =
    losingTrades.length > 0
      ? Math.min(
          ...losingTrades.map(
            (trade) =>
              ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100
          )
        )
      : 0;
  const riskScoreBase = profitFactor * 25 - Math.abs(worstLossPercent) / 2;
  const riskScore = Math.min(80, Math.max(10, riskScoreBase));

  // Emotional Control (based on sequence of trades, streak handling)
  // Tries to detect revenge trading patterns
  let consecutiveLosses = 0;
  let maxConsecutiveLosses = 0;
  let emotionalPatterns = 0;

  // Sort trades by date
  const sortedTrades = [...trades].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Analyze trading patterns
  for (let i = 0; i < sortedTrades.length; i++) {
    if (sortedTrades[i].pnl < 0) {
      consecutiveLosses++;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, consecutiveLosses);

      // Check for potential revenge trading
      if (i < sortedTrades.length - 1) {
        const currentLoss = sortedTrades[i].pnl;
        const nextTrade = sortedTrades[i + 1];

        // Signs of revenge trading: larger position after a loss
        if (nextTrade.quantity > sortedTrades[i].quantity * 1.5) {
          emotionalPatterns++;
        }
      }
    } else {
      consecutiveLosses = 0;
    }
  }

  const emotionalScoreBase =
    70 - maxConsecutiveLosses * 10 - emotionalPatterns * 15;
  const emotionalScore = Math.min(85, Math.max(15, emotionalScoreBase));

  // Strategy Adherence (based on consistency of returns)
  // Heavily penalizes inconsistency now
  const consistencyScore = 80 - Math.min(60, coefficientOfVariation * 40);
  const adherenceScore = Math.max(20, consistencyScore);

  // Pattern Recognition (based on similar setups and outcomes)
  // This is a simplified approximation
  const patternBase = profitFactor * 20 + winRate / 3;
  const patternScore = Math.min(85, Math.max(20, patternBase));

  // Extra skill categories for the radar chart
  const consistencyMetric = Math.max(10, adherenceScore);
  const probabilityMetric = Math.max(15, winRate * 0.8);
  const profitabilityMetric = Math.min(90, Math.max(10, profitFactor * 25));
  const stabilityMetric = Math.max(15, 70 - maxConsecutiveLosses * 8);
  const disciplineMetric = Math.max(15, riskScore);

  // Calculate overall mastery score (weighted)
  const weights = {
    entryTiming: 1,
    exitExecution: 1.2,
    riskManagement: 1.5,
    emotionalControl: 1.3,
    strategyAdherence: 1,
    patternRecognition: 1,
    consistency: 0.8,
    probability: 0.9,
    profitability: 1.4,
    stability: 1.1,
    discipline: 1.2,
  };

  const metrics = {
    entryTiming: entryTimingScore,
    exitExecution: exitScore,
    riskManagement: riskScore,
    emotionalControl: emotionalScore,
    strategyAdherence: adherenceScore,
    patternRecognition: patternScore,
    consistency: consistencyMetric,
    probability: probabilityMetric,
    profitability: profitabilityMetric,
    stability: stabilityMetric,
    discipline: disciplineMetric,
  };

  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const weightedSum = Object.keys(metrics).reduce(
    (sum, skill) => sum + metrics[skill] * weights[skill],
    0
  );

  const overallScore = Math.round(weightedSum / totalWeight);

  return {
    ...metrics,
    overallScore,
  };
};

/**
 * Generate trading insights based on trade history
 * @param {Array} trades - Array of trade objects
 * @returns {Array} Array of insight objects
 */
export const generateTradingInsights = (trades) => {
  if (!trades || trades.length < 5) {
    return [
      {
        title: "Not enough trade data for meaningful insights",
        description:
          "Continue trading and logging your results. More data will lead to more accurate insights.",
        type: "info",
        icon: "info",
      },
    ];
  }

  const insights = [];

  // Win rate analysis
  const winningTrades = trades.filter((trade) => trade.pnl > 0);
  const winRate = (winningTrades.length / trades.length) * 100;

  if (winRate < 40) {
    insights.push({
      title: `Low win rate detected (${winRate.toFixed(1)}%)`,
      description:
        "Your win percentage is below optimal levels. Focus on improving entry criteria and consider using smaller position sizes until your win rate improves.",
      type: "warning",
      icon: "alert",
    });
  }

  // Time-based analysis (simplified)
  const amTrades = trades.filter((trade) => {
    const tradeHour = new Date(trade.date).getHours();
    return tradeHour < 12;
  });

  const pmTrades = trades.filter((trade) => {
    const tradeHour = new Date(trade.date).getHours();
    return tradeHour >= 12;
  });

  if (amTrades.length >= 3 && pmTrades.length >= 3) {
    const amWinRate =
      (amTrades.filter((t) => t.pnl > 0).length / amTrades.length) * 100;
    const pmWinRate =
      (pmTrades.filter((t) => t.pnl > 0).length / pmTrades.length) * 100;

    const difference = Math.abs(amWinRate - pmWinRate);
    if (difference > 15) {
      const betterTime = amWinRate > pmWinRate ? "morning" : "afternoon";
      insights.push({
        title: `${
          betterTime.charAt(0).toUpperCase() + betterTime.slice(1)
        } trading outperforms by ${difference.toFixed(1)}%`,
        description: `Your win rate is significantly higher during the ${betterTime} (${Math.max(
          amWinRate,
          pmWinRate
        ).toFixed(1)}% vs ${Math.min(amWinRate, pmWinRate).toFixed(
          1
        )}%). Consider focusing your trading during this period.`,
        type: "success",
        icon: "clock",
      });
    }
  }

  // Position sizing analysis
  const positionSizes = trades.map((t) => t.quantity);
  const avgSize =
    positionSizes.reduce((sum, size) => sum + size, 0) / positionSizes.length;
  const sizeStdDev = Math.sqrt(
    positionSizes.reduce((sum, size) => sum + Math.pow(size - avgSize, 2), 0) /
      positionSizes.length
  );

  if (sizeStdDev > avgSize * 0.5) {
    insights.push({
      title: "Inconsistent position sizing detected",
      description:
        "Your position sizes vary significantly between trades. Standardizing position sizing based on account risk percentage can improve risk management.",
      type: "warning",
      icon: "alert",
    });
  }

  // Return analysis
  const winSizes = winningTrades.map((t) => Math.abs(t.pnl));
  const lossSizes = trades.filter((t) => t.pnl < 0).map((t) => Math.abs(t.pnl));

  const avgWin =
    winSizes.length > 0
      ? winSizes.reduce((sum, size) => sum + size, 0) / winSizes.length
      : 0;
  const avgLoss =
    lossSizes.length > 0
      ? lossSizes.reduce((sum, size) => sum + size, 0) / lossSizes.length
      : 0;

  if (avgWin > 0 && avgLoss > 0) {
    const winLossRatio = avgWin / avgLoss;

    if (winLossRatio < 1.0 && winRate < 60) {
      insights.push({
        title: "Unfavorable win size to loss size ratio",
        description: `Your average winning trade (${avgWin.toFixed(
          2
        )}) is smaller than your average losing trade (${avgLoss.toFixed(
          2
        )}). Combined with your win rate of ${winRate.toFixed(
          1
        )}%, this isn't sustainable.`,
        type: "warning",
        icon: "alert",
      });
    } else if (winLossRatio < 1.5 && winRate < 55) {
      insights.push({
        title: "Risk-reward ratio needs improvement",
        description: `Your average win is only ${winLossRatio.toFixed(
          2
        )}x your average loss. With a ${winRate.toFixed(
          1
        )}% win rate, aim for at least a 1.5:1 reward-to-risk ratio.`,
        type: "warning",
        icon: "alert",
      });
    }
  }

  return insights;
};

/**
 * Generate recommended actions based on skills assessment
 * @param {Object} skillsData - Skills assessment data
 * @returns {Array} Array of recommended action objects
 */
export const generateRecommendedActions = (skillsData) => {
  if (!skillsData) {
    return [];
  }

  const actions = [];

  // Entry timing recommendations
  if (skillsData.entryTiming < 40) {
    actions.push({
      title: "Improve entry techniques",
      description:
        "Your entries need significant work. Review your last 20 trades and identify the specific entry signals that worked vs. those that failed.",
      difficulty: "hard",
      priority: "high",
    });
  } else if (skillsData.entryTiming < 60) {
    actions.push({
      title: "Refine entry criteria",
      description:
        "Your entries are inconsistent. Create a checklist of 3-5 specific conditions that must be met before entering a trade.",
      difficulty: "medium",
      priority: "high",
    });
  }

  // Exit execution recommendations
  if (skillsData.exitExecution < 50) {
    actions.push({
      title: "Implement strict exit rules",
      description:
        "Your exits are costing you significantly. Define exact exit criteria for both winning and losing trades, and follow them without exception.",
      difficulty: "hard",
      priority: "high",
    });
  } else if (skillsData.exitExecution < 65) {
    actions.push({
      title: "Optimize profit-taking strategy",
      description:
        "You're likely cutting winners short. Test scaling out of positions at different profit targets rather than exiting all at once.",
      difficulty: "medium",
      priority: "medium",
    });
  }

  // Risk management recommendations
  if (skillsData.riskManagement < 45) {
    actions.push({
      title: "Implement position sizing formula",
      description:
        "Your risk management needs urgent attention. Limit each trade to risk no more than 1% of your account value, calculated before entry.",
      difficulty: "medium",
      priority: "critical",
    });
  } else if (skillsData.riskManagement < 65) {
    actions.push({
      title: "Standardize stop-loss placement",
      description:
        "Your stop losses appear inconsistent. Place stops at technical levels rather than fixed dollar amounts or percentages.",
      difficulty: "medium",
      priority: "high",
    });
  }

  // Emotional control recommendations
  if (skillsData.emotionalControl < 40) {
    actions.push({
      title: "Implement mandatory cool-down periods",
      description:
        "Take a 2-hour break after any losing trade to prevent revenge trading. Log your emotional state before and after each trade.",
      difficulty: "hard",
      priority: "high",
    });
  } else if (skillsData.emotionalControl < 60) {
    actions.push({
      title: "Create a pre-trade checklist",
      description:
        "Develop a process to ensure each trade is taken for the right reasons, not driven by FOMO or the need to recover losses.",
      difficulty: "medium",
      priority: "medium",
    });
  }

  // Strategy adherence recommendations
  if (skillsData.strategyAdherence < 50) {
    actions.push({
      title: "Focus on a single strategy",
      description:
        "Your trading lacks consistency. Select your best-performing setup and trade only that pattern for the next 20 trades.",
      difficulty: "medium",
      priority: "high",
    });
  }

  // Consistency recommendations
  if (skillsData.consistency < 45) {
    actions.push({
      title: "Standardize trading process",
      description:
        "Your results show high variability. Create and follow a detailed trading plan with specific entry, management, and exit rules.",
      difficulty: "hard",
      priority: "high",
    });
  }

  return actions;
};
